import { AppUser, getUsersInTag, IOptionsWithTypes } from '@eventr/common'
import { StyledPullToRefresh, Typography } from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useShallow } from 'zustand/react/shallow'
import { HeaderSearchBar } from '../../../home/components/carousel/slide/footer/HeaderSearchBar'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { query } from '../../../state/headerState/headerSignals'
import { DepartmentLine } from './DepartmentLine'
import { GroupLine } from './GroupLine'
import { UserListSection } from './UserListSection'
import { SelectAllBox } from './selectAllBox'
import {
    checkIfUserSelected,
    checkIfUserTagSelected,
    filterBySearch,
} from './utils'

interface IMemberListProps {
    users: AppUser[]
    wpUser?: AppUser
    allowSelfRemove?: boolean
    selectedUsers: any[]
    searchBar?: boolean
    setSelectedUsers: (selectedUsers: IOptionsWithTypes[]) => void
    onRefresh?: () => Promise<void>
    userControlBox?: (user: AppUser) => JSX.Element
}

export const MembersList = ({
    users,
    wpUser,
    allowSelfRemove,
    selectedUsers,
    searchBar = true,
    userControlBox,
    onRefresh,
    setSelectedUsers,
}: IMemberListProps) => {
    const { orgConfig } = useBoundStore(
        useShallow((s) => ({
            orgConfig: s.orgConfig,
        }))
    )

    const onAddUser = (
        userList: AppUser[],
        updatedUsers?: IOptionsWithTypes[]
    ) => {
        const notSelectedUsers = userList.filter(
            (u) => !checkIfUserSelected(u.id, selectedUsers)
        )

        const extendedList = notSelectedUsers.map(
            (user) =>
                ({
                    type: 'User',
                    option: user.id,
                } as IOptionsWithTypes)
        )
        setSelectedUsers([...(updatedUsers ?? selectedUsers), ...extendedList])
    }

    const onRemoveUser = (
        userList: AppUser[],
        updatedUsers?: IOptionsWithTypes[]
    ) => {
        const idsToFilter = userList.map((u) => {
            return u.id
        })
        const filteredList = (updatedUsers ?? selectedUsers).filter(
            (member) => {
                return (
                    !idsToFilter.includes(member.option) ||
                    member.option === wpUser?.id
                )
            }
        )

        setSelectedUsers(filteredList)
    }

    const onAddDepartment = (department: string) => {
        const usersToAdd = getUsersInTag(
            { name: department, type: 'Department' },
            users
        )
        onAddUser(usersToAdd, [
            ...selectedUsers,
            {
                type: 'DepartmentV2',
                option: department,
            },
        ])
    }
    const onRemoveDepartment = (department: string) => {
        const tmpMembersListTemp = selectedUsers.filter((member) => {
            if (
                (member.type === 'Department' ||
                    member.type === 'DepartmentV2') &&
                member.option === department
            ) {
                return false
            } else {
                return true
            }
        })

        const tagUsers = getUsersInTag(
            { name: department, type: 'Department' },
            users
        )
        onRemoveUser(tagUsers, tmpMembersListTemp)
    }
    const onAddGroup = (group: string) => {
        const usersToAdd = getUsersInTag({ name: group, type: 'Group' }, users)
        onAddUser(usersToAdd, [
            ...selectedUsers,
            {
                type: 'GroupV2',
                option: group,
            },
        ])
    }

    const onRemoveGroup = (group: string) => {
        const tmpMembersListTemp = selectedUsers.filter((member) => {
            if (
                (member.type === 'Group' || member.type === 'GroupV2') &&
                member.option === group
            ) {
                return false
            } else {
                return true
            }
        })

        const tagUsers = getUsersInTag({ name: group, type: 'Group' }, users)
        onRemoveUser(tagUsers, tmpMembersListTemp)
    }

    const selectAllBox = () => {
        const allSelected = users.every((user) => {
            return (
                checkIfUserSelected(user.id, selectedUsers) ||
                checkIfUserTagSelected(user.id, selectedUsers, users)
            )
        })
        return (
            <SelectAllBox
                allSelected={allSelected}
                allUsers={users}
                query={query.value}
                onClick={() => {
                    if (allSelected) {
                        setSelectedUsers(
                            allowSelfRemove
                                ? []
                                : selectedUsers.filter((person) => {
                                      return person.option === wpUser?.id
                                  })
                        )
                    } else {
                        const allUsers = users.map((user) => {
                            return {
                                type: 'User',
                                option: user.id,
                            } as IOptionsWithTypes
                        })
                        setSelectedUsers(allUsers)
                    }
                }}
            />
        )
    }

    const departmentsListSection = () => {
        if (
            orgConfig &&
            orgConfig.departmentTags &&
            Array.isArray(orgConfig.departmentTags)
        ) {
            return orgConfig.departmentTags.map((depTag, i) => {
                return (
                    <DepartmentLine
                        key={i}
                        allUsers={users}
                        membersList={selectedUsers}
                        tag={depTag}
                        onAddDepartment={onAddDepartment}
                        onRemoveDepartment={onRemoveDepartment}
                        onRemoveUser={(u) => onRemoveUser([u])}
                        onAddUser={(u) => onAddUser([u])}
                        allowSelfRemove={allowSelfRemove}
                        userControlBox={userControlBox}
                    />
                )
            })
        } else {
            return null
        }
    }

    const groupsListSection = () => {
        if (orgConfig && orgConfig.groupTags) {
            return orgConfig.groupTags.map((groupTag, i) => {
                return (
                    <GroupLine
                        key={i}
                        allUsers={users}
                        tag={groupTag}
                        membersList={selectedUsers}
                        onAddGroup={onAddGroup}
                        onRemoveGroup={onRemoveGroup}
                        onRemoveUser={(u) => onRemoveUser([u])}
                        onAddUser={(u) => onAddUser([u])}
                        allowSelfRemove={allowSelfRemove}
                        userControlBox={userControlBox}
                    />
                )
            })
        } else {
            return null
        }
    }

    const usersListSection = () => (
        <UserListSection
            allUsers={users}
            onAddUser={onAddUser}
            onRemoveUser={onRemoveUser}
            membersList={selectedUsers}
            allowSelfRemove={allowSelfRemove}
            userControlBox={userControlBox}
        />
    )

    const membersListSection = () => {
        return (
            <>
                {selectAllBox()}
                {departmentsListSection()}
                {groupsListSection()}
                {usersListSection()}
            </>
        )
    }

    const membersList = (
        <Box sx={{ maxWidth: '100vw', gap: '16px' }}>
            <Box sx={{ display: 'grid', justifyItems: 'center', gap: '8px' }}>
                {users && filterBySearch(users).length > 0 ? (
                    membersListSection()
                ) : (
                    <Box>
                        <Typography>
                            {i18n.t('edit-members.no-users')}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    )

    return (
        <Stack style={{ width: '100%', gap: '12px', margin: 0 }}>
            {searchBar && <HeaderSearchBar />}
            <StyledPullToRefresh
                isPullable={!!onRefresh}
                onRefresh={async () => {
                    /// Nothing
                }}
                promiseFunction={onRefresh}
                maxPullDownDistance={100}
            >
                <Box style={{ width: '100%' }}>{membersList}</Box>
            </StyledPullToRefresh>
        </Stack>
    )
}
